import { useState, useEffect, useMemo } from 'react';
import { Plus, Edit, Trash2, Search, Loader2, Package, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { produtoServicoService, ProdutoServico } from '../../../services/propostaService';

type SortField = 'codigo' | 'nome' | 'valorUnit';
type SortDirection = 'asc' | 'desc';

export function ProdutoManagement() {
  const [produtos, setProdutos] = useState<ProdutoServico[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingProduto, setEditingProduto] = useState<ProdutoServico | null>(null);
  const [sortField, setSortField] = useState<SortField>('codigo');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  
  const [formData, setFormData] = useState({
    tipo: 'produto' as const,
    codigo: '',
    nome: '',
    descricao: '',
    valorUnit: 0,
    unidade: 'un',
  });

  useEffect(() => { loadProdutos(); }, []);

  const gerarProximoCodigo = () => {
    const codigosExistentes = produtos
      .map(p => p.codigo)
      .filter(c => c && c.startsWith('PRD-'))
      .map(c => parseInt(c!.replace('PRD-', '')) || 0);
    const maiorNumero = codigosExistentes.length > 0 ? Math.max(...codigosExistentes) : 0;
    return `PRD-${String(maiorNumero + 1).padStart(3, '0')}`;
  };

  const formatCurrencyInput = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0);
  };

  const loadProdutos = async () => {
    setIsLoading(true);
    try {
      const data = await produtoServicoService.list({ tipo: 'produto', active: true });
      setProdutos(data);
    } catch (error) {
      console.error('Erro ao carregar:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingProduto) {
        await produtoServicoService.update(editingProduto.id, formData);
      } else {
        await produtoServicoService.create(formData);
      }
      setShowForm(false);
      setEditingProduto(null);
      resetForm();
      loadProdutos();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      alert('Erro ao salvar');
    }
  };

  const handleEdit = (produto: ProdutoServico) => {
    setEditingProduto(produto);
    setFormData({
      tipo: 'produto',
      codigo: produto.codigo || '',
      nome: produto.nome,
      descricao: produto.descricao || '',
      valorUnit: Number(produto.valorUnit),
      unidade: produto.unidade,
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Deseja desativar este produto?')) return;
    try {
      await produtoServicoService.delete(id);
      loadProdutos();
    } catch (error) {
      console.error('Erro ao excluir:', error);
    }
  };

  const resetForm = () => {
    setFormData({ tipo: 'produto', codigo: gerarProximoCodigo(), nome: '', descricao: '', valorUnit: 0, unidade: 'un' });
  };

  const formatCurrency = (value: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

  const filteredAndSortedProdutos = useMemo(() => {
    const filtered = produtos.filter(p =>
      p.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.codigo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.descricao?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    return filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'codigo': comparison = (a.codigo || '').localeCompare(b.codigo || ''); break;
        case 'nome': comparison = a.nome.localeCompare(b.nome); break;
        case 'valorUnit': comparison = Number(a.valorUnit) - Number(b.valorUnit); break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [produtos, searchTerm, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 text-muted-foreground" />;
    return sortDirection === 'asc' ? <ArrowUp className="w-3 h-3 text-purple-600" /> : <ArrowDown className="w-3 h-3 text-purple-600" />;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-foreground">Produtos</h2>
        <button onClick={() => { resetForm(); setEditingProduto(null); setShowForm(true); }}
          className="flex items-center gap-2 px-4 py-2 btn btn-primary">
          <Plus className="w-4 h-4" /> Novo Produto
        </button>
      </div>

      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input type="text" placeholder="Buscar produtos..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-input rounded-lg text-foreground bg-card" />
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg p-6 w-full max-w-lg">
            <h3 className="text-lg font-bold text-foreground mb-4">{editingProduto ? 'Editar' : 'Novo'} Produto</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Código</label>
                  <input type="text" value={formData.codigo} onChange={e => setFormData({ ...formData, codigo: e.target.value })}
                    className="w-full px-3 py-2 border border-input rounded-lg text-foreground bg-muted/50" placeholder="PRD-001" readOnly />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Unidade</label>
                  <select value={formData.unidade} onChange={e => setFormData({ ...formData, unidade: e.target.value })}
                    className="w-full px-3 py-2 border border-input rounded-lg text-foreground bg-card">
                    <option value="un">Unidade (un)</option>
                    <option value="cx">Caixa (cx)</option>
                    <option value="kg">Quilograma (kg)</option>
                    <option value="m">Metro (m)</option>
                    <option value="m²">Metro² (m²)</option>
                    <option value="l">Litro (l)</option>
                    <option value="pç">Peça (pç)</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Nome *</label>
                <input type="text" required value={formData.nome} onChange={e => setFormData({ ...formData, nome: e.target.value })}
                  className="w-full px-3 py-2 border border-input rounded-lg text-foreground bg-card" placeholder="Nome do produto" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Descrição</label>
                <textarea value={formData.descricao} onChange={e => setFormData({ ...formData, descricao: e.target.value })}
                  className="w-full px-3 py-2 border border-input rounded-lg text-foreground bg-card" rows={3} placeholder="Descrição detalhada..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Valor Unitário *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">R$</span>
                  <input type="text" inputMode="decimal" required value={formatCurrencyInput(formData.valorUnit)}
                    onChange={e => { const rawValue = e.target.value.replace(/\D/g, ''); setFormData({ ...formData, valorUnit: Number(rawValue) / 100 }); }}
                    className="w-full pl-10 pr-3 py-2 border border-input rounded-lg text-foreground bg-card text-right" placeholder="0,00" />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <button type="button" onClick={() => { setShowForm(false); setEditingProduto(null); }}
                  className="px-4 py-2 btn btn-outline">Cancelar</button>
                <button type="submit" className="px-4 py-2 btn btn-primary">Salvar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="bg-card border rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 text-purple-600 animate-spin" /></div>
        ) : filteredAndSortedProdutos.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-muted-foreground">Nenhum produto cadastrado</p>
          </div>
        ) : (
          <div className="overflow-x-auto"><table className="w-full">
            <thead className="bg-muted/50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                  <button onClick={() => handleSort('codigo')} className="flex items-center gap-1 hover:text-foreground">Código <SortIcon field="codigo" /></button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                  <button onClick={() => handleSort('nome')} className="flex items-center gap-1 hover:text-foreground">Nome <SortIcon field="nome" /></button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Unidade</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase">
                  <button onClick={() => handleSort('valorUnit')} className="flex items-center gap-1 ml-auto hover:text-foreground">Valor <SortIcon field="valorUnit" /></button>
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredAndSortedProdutos.map(produto => (
                <tr key={produto.id} className="hover:bg-muted/50">
                  <td className="px-4 py-3 text-sm text-muted-foreground font-mono">{produto.codigo || '-'}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-foreground">{produto.nome}</div>
                    {produto.descricao && <div className="text-sm text-muted-foreground truncate max-w-xs">{produto.descricao}</div>}
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{produto.unidade}</td>
                  <td className="px-4 py-3 text-right font-medium text-foreground">{formatCurrency(Number(produto.valorUnit))}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => handleEdit(produto)} className="p-2 hover:bg-muted rounded" title="Editar"><Edit className="w-4 h-4 text-blue-600 dark:text-blue-400" /></button>
                      <button onClick={() => handleDelete(produto.id)} className="p-2 hover:bg-muted rounded" title="Excluir"><Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}
